import { useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { readDeviceLabelSerial } from "@/lib/sdtestapp/sdTestAppHttpClient";
import type { SdTestDevice } from "@/lib/sdtestapp/sdTestAppHttpClient";
import { openIncident } from "@/pages/manufacturing/qcIncidentsApi";
import { readPolledDeviceMcuUuid, readPolledDeviceModelNumber } from "@/pages/manufacturing/configurationDeviceFields";

export function BenchBatchQcIncidentModal({
  open,
  device,
  onClose,
  onSubmitted,
}: {
  open: boolean;
  device: SdTestDevice | null;
  onClose: () => void;
  onSubmitted: (incidentId: number) => void;
}): JSX.Element | null {
  const [issue, setIssue] = useState("");
  const [issueNotes, setIssueNotes] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open || !device) return null;

  const serial = readDeviceLabelSerial(device);
  const modelNumber = readPolledDeviceModelNumber(device);
  const mcuUuid = readPolledDeviceMcuUuid(device);

  const submit = async () => {
    const trimmedIssue = issue.trim();
    if (!trimmedIssue) {
      setError("Enter a short issue summary.");
      return;
    }
    if (!serial) {
      setError("Device label serial is required to file a QC incident.");
      return;
    }
    setPending(true);
    setError(null);
    try {
      const incident = await openIncident({
        type: "device",
        serial,
        issue: trimmedIssue,
        issue_notes: issueNotes.trim() || null,
      });
      onSubmitted(incident.id);
      setIssue("");
      setIssueNotes("");
      onClose();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : String(submitError));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-lg shadow-lg">
        <CardHeader>
          <h2 className="text-lg font-semibold">Log QC incident</h2>
          <p className="text-sm text-muted-foreground">
            Calibration and test — device incident for bench batch testing.
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="rounded border bg-muted/30 p-3 text-sm">
            <p>
              <span className="font-medium">Model:</span> {modelNumber || "—"}
            </p>
            <p>
              <span className="font-medium">Device label:</span> {serial || "—"}
            </p>
            <p>
              <span className="font-medium">MCU UUID:</span> {mcuUuid || "—"}
            </p>
          </div>
          <div>
            <Label htmlFor="bench-qc-issue">Issue (short summary)</Label>
            <Input
              id="bench-qc-issue"
              value={issue}
              onChange={(event) => setIssue(event.target.value)}
              placeholder="Required"
            />
          </div>
          <div>
            <Label htmlFor="bench-qc-notes">Issue notes (optional)</Label>
            <textarea
              id="bench-qc-notes"
              className="mt-1 min-h-[96px] w-full rounded border px-2 py-1 text-sm"
              value={issueNotes}
              onChange={(event) => setIssueNotes(event.target.value)}
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <div className="flex flex-wrap gap-2">
            <Button type="button" disabled={pending} onClick={() => void submit()}>
              {pending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                "Submit incident"
              )}
            </Button>
            <Button type="button" variant="secondary" disabled={pending} onClick={onClose}>
              Cancel
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
